import { useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputMenu from "@repo/components/src/forms/menu";
import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import {
  formatNumber,
  formatPercentage,
} from "@repo/components/src/utils/numbers";
import { AccountMetricTotalValueDocument, LastSocialNetworkUserPostsDocument, AccountMetricTotalValueQuery } from "@repo/graphql/documents/accountMetric/query.generated";
import { CalendarDays, InfoIcon } from "lucide-react-native";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import AudienceSummary from "./audience";
import TopPost from "./topPost";
import { getEngagementBadgeProps, getReachBadgeProps } from "./utils";
import { SocialNetworkUserFragmentFragment } from "@repo/graphql/documents/socialNetworkUser/fragment.generated";

const daysSchema = yup.object({
  days: yup.number().required(),
});

type DaysSchema = yup.InferType<typeof daysSchema>;

type Props = {
  tikTok: SocialNetworkUserFragmentFragment;
  postulationId?: string;
  brandId?: string;
};

const getMetric = (
  metrics:
    | AccountMetricTotalValueQuery["accountMetric"]["totalValues"]
    | undefined,
  metric: string,
) => {
  const thisMetric = metrics?.find((m) => {
    const accountMetric = m.accountMetric;
    return accountMetric.name === metric;
  });
  if (!thisMetric) return 0;
  return thisMetric
    .value;
};

const avgOf = (values: number[]) => {
  if (values.length === 0) return 0;
  return values.reduce((acc, curr) => acc + curr, 0) / values.length;
};

const SharedTikTokStatistics = React.memo((props: Props) => {
  const { tikTok, postulationId, brandId } = props;
  const formProps = useForm<DaysSchema>({
    resolver: yupResolver(daysSchema),
    defaultValues: {
      days: 30,
    },
  });
  const days = useWatch({
    control: formProps.control,
    name: "days",
  });
  const lastPostMetricsQuery = useQuery(LastSocialNetworkUserPostsDocument, {
    variables: {
      socialNetworkUserId: tikTok.id,
      brandId: brandId,
      postulationId: postulationId,
      days: days,
    },
    skip: !tikTok.accessTokenStatus,
  });
  const accountMetricQuery = useQuery(AccountMetricTotalValueDocument, {
    variables: {
      socialNetworkUserId: tikTok.id,
      brandId: brandId,
      postulationId: postulationId,
      days: days,
    },
    skip: !tikTok.accessTokenStatus,
  });

  const isLoading = accountMetricQuery.loading;
  const posts = lastPostMetricsQuery.data?.accountMetric.lastPosts ?? [];

  const sumInteractions = posts.reduce(
    (acc, p) => acc + p.likes + p.comments + p.shares,
    0,
  );
  const sumViews = posts.reduce((acc, p) => acc + p.views, 0);
  const avgEngagement = sumInteractions / (sumViews || 1);

  const avgReach = avgOf(posts.map((p) => p.reach));

  const { text: engagementBadgeText, ...engagementBadgeProps } =
    getEngagementBadgeProps(avgEngagement, tikTok.followers);

  const { text: reachBadgeText, ...reachBadgeProps } = getReachBadgeProps(
    avgReach,
    tikTok.followers,
  );

  return (
    <HStack flexSpace="md">
      <Box className="w-1/2">
        <Card variant="outline" size="lg" className="w-full rounded-xl h-full">
          <HStack space="sm" className="items-center">
            <Heading bold className="" numberOfLines={1}>
              Engagement
            </Heading>
            <Tooltip
              placement="right"
              trigger={(triggerProps) => (
                <Box {...triggerProps}>
                  <Icon as={InfoIcon} action="muted" />
                </Box>
              )}
            >
              <TooltipContent arrow="left" className="w-48">
                <TooltipText>
                  Interacciones en relación al número de views.
                </TooltipText>
              </TooltipContent>
            </Tooltip>
            <Box className="ml-auto">
              <FormInputMenu
                control={formProps.control}
                name="days"
                options={[
                  { label: "7 días", value: 7 },
                  { label: "14 días", value: 14 },
                  { label: "30 días", value: 30 },
                ]}
                size="sm"
                labelSize="md"
                headingProps={{ bold: false }}
                buttonTriggerProps={{
                  rounded: "lg",
                  action: "muted",
                  variant: "outline",
                  className: "bg-background-50 px-2 h-6",
                  size: "sm",
                }}
                buttonTriggerIcon={CalendarDays}
              />
            </Box>
          </HStack>
          <Text action="muted" className="mt-3">
            Promedio
          </Text>
          <Skeleton isLoaded={!isLoading} className="h-8">
            <HStack space="md" className="items-center">
              {posts.length > 0 ? (
                <>
                  <Text size="2xl">{formatPercentage(avgEngagement)}</Text>
                  <Badge
                    {...engagementBadgeProps}
                    size="md"
                    rounded="lg"
                    className={`gap-x-1 border ${engagementBadgeProps.className ?? ""}`}
                  >
                    <BadgeText>{engagementBadgeText}</BadgeText>
                    <BadgeIcon as={InfoIcon} />
                  </Badge>
                </>
              ) : (
                <Text action="muted" size="sm" className="mt-2">
                  El usuario no ha subido posts en los últimos {days} días.
                </Text>
              )}
            </HStack>
          </Skeleton>
        </Card>
      </Box>
      <Box className="w-1/2">
        <Card variant="outline" size="lg" className="w-full rounded-xl h-full">
          <HStack space="sm" className="items-center">
            <Heading bold className="" numberOfLines={1}>
              Views
            </Heading>
            <Tooltip
              placement="right"
              trigger={(triggerProps) => (
                <Box {...triggerProps}>
                  <Icon as={InfoIcon} action="muted" />
                </Box>
              )}
            >
              <TooltipContent arrow="left" className="w-48">
                <TooltipText>
                  Cantidad total de veces que se vio el contenido
                </TooltipText>
              </TooltipContent>
            </Tooltip>
          </HStack>
          <Text action="muted" className="mt-3">
            Promedio
          </Text>
          <Skeleton isLoaded={!isLoading} className="h-8">
            <HStack space="md" className="items-center">
              {posts.length > 0 ? (
                <>
                  <Text size="2xl">{formatNumber(avgReach)}</Text>
                  <Badge
                    {...reachBadgeProps}
                    size="md"
                    rounded="lg"
                    className={`gap-x-1 border ${reachBadgeProps.className ?? ""}`}
                  >
                    <BadgeText>{reachBadgeText}</BadgeText>
                    <BadgeIcon as={InfoIcon} />
                  </Badge>
                </>
              ) : (
                <Text action="muted" size="sm" className="mt-2">
                  El usuario no ha subido posts en los últimos {days} días.
                </Text>
              )}
            </HStack>
          </Skeleton>
        </Card>
      </Box>
      <Box className="w-full">
        <Card variant="outline" size="lg" className="w-full rounded-xl">
          <AudienceSummary accountMetricQuery={accountMetricQuery} />
        </Card>
      </Box>
      <Box className="w-full">
        <Card variant="outline" size="lg" className="w-full rounded-xl">
          <TopPost {...props} />
        </Card>
      </Box>
    </HStack>
  );
});

SharedTikTokStatistics.displayName = "SharedTikTokStatistics";

export default SharedTikTokStatistics;
